import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Home.module.css';
import { SiteNav } from '../../components/marketing/SiteNav.jsx';
import { SiteFooter } from '../../components/marketing/SiteFooter.jsx';
import { Button, SplitBar, TierCard } from '../../components/ui';
import { AccuracyWidget } from '../../components/app/AccuracyWidget.jsx';
import { getPublicAccuracy, normaliseAccuracy } from '../../api/public.js';
import { TELEGRAM_BOT_URL } from '../../config.js';
import {
  FAQ,
  FREE_FORECAST_ALLOWANCE,
  MARKETING_STATS,
  MARKETING_TIERS,
  SPORTS,
} from '../../content/marketingFacts.js';

const STEPS = [
  {
    title: 'Pick a match',
    body: 'Choose your sport, date, league, and the game you care about. Nine sports, every major league.',
  },
  {
    title: 'The duck runs the odds',
    body: 'Don Quack pulls live bookmaker odds and runs an AI model to turn them into a clear probability.',
  },
  {
    title: 'Get the verdict',
    body: 'A predicted winner, win probabilities, a confidence rating, and sport-specific insights — in seconds.',
  },
];

const FEATURES = [
  {
    icon: '📊',
    title: 'Probabilities, not vibes',
    body: 'Every forecast is a real number you can reason about — backed by live odds and AI analysis.',
  },
  {
    icon: '⭐',
    title: 'Daily Top Picks',
    body: 'The highest-confidence matches across every league, surfaced for you each morning.',
  },
  {
    icon: '🌍',
    title: 'Four languages',
    body: 'English, Russian, Spanish, and Portuguese — the duck speaks your language.',
  },
  {
    icon: '🧠',
    title: 'The "why" included',
    body: 'Sport-specific insights and analysis, so you understand the call, not just the number.',
  },
  {
    icon: '🕑',
    title: 'Forecast history',
    body: 'Every forecast you generate is saved, so you can track how the calls played out.',
  },
  {
    icon: '₿',
    title: 'Crypto subscriptions',
    body: 'Simple, private checkout with crypto. Upgrade in a couple of taps.',
  },
];

/**
 * The public marketing page.
 *
 * Static apart from the accuracy figures, which come from the unauthenticated
 * GET /api/public/accuracy. Every "start" CTA leads to /app, the login.
 * Prices and product counts come from src/content/marketingFacts.js — see the
 * notes in that file about keeping them in step with the backend.
 */
export function Home() {
  const navigate = useNavigate();

  // No server-side rendering here, so the page title and description are set on
  // mount. Search crawlers that execute JavaScript will pick these up; the
  // static fallbacks in index.html cover the ones that don't.
  useEffect(() => {
    document.title = 'Don Quack — AI Sports Forecasts & Predictions You Can Trust';
  }, []);

  return (
    <>
      <SiteNav />

      <main>
        {/* ---------------- HERO ---------------- */}
        <section className={styles.hero}>
          <div className={styles.heroWrap}>
            <div>
              <span className="eyebrow">AI Sports Forecasts</span>
              <h1 className={styles.h1}>
                Sports forecasts you can <span className="grad-text">actually trust.</span>
              </h1>
              <p className={styles.lede}>
                Don Quack reads live bookmaker odds and runs them through AI to give you a real win
                probability, a predicted winner, and the analysis behind it — across{' '}
                {MARKETING_STATS.sportsCount} sports, in {MARKETING_STATS.languagesCount} languages.
              </p>
              <div className={styles.ctaRow}>
                <Button to="/app" size="lg">
                  Start free 🦆
                </Button>
                <Button href={TELEGRAM_BOT_URL} variant="secondary" size="lg">
                  Or follow on Telegram
                </Button>
              </div>
              <p className={styles.micro}>
                Email sign-up · no card · {FREE_FORECAST_ALLOWANCE} on the free plan
              </p>
            </div>

            <div className={styles.art}>
              <div className={styles.glow} aria-hidden="true" />
              <img
                src="/duck.webp"
                alt="The Don Quack mascot"
                width="300"
                height="300"
                fetchPriority="high"
              />

              {/* A peek at the real product: one forecast, resolved. */}
              <div className={styles.peek}>
                <div className={styles.peekLeague}>Premier League · Today</div>
                <div className={styles.peekTeams}>
                  <span>Man City</span>
                  <span>Arsenal</span>
                </div>
                <SplitBar
                  homeProbability={64}
                  winner="home"
                  size="sm"
                  homeLabel="Man City"
                  awayLabel="Arsenal"
                />
                <div className={styles.peekPick}>
                  Predicted winner · <b>Man City</b>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- TRUST STRIP ---------------- */}
        <div className={styles.strip}>
          <div className={styles.stripWrap}>
            {/* Live figure from GET /api/public/accuracy. Falls back to a
                non-numeric claim if the call fails or nothing is graded yet —
                never a stand-in percentage. */}
            <AccuracyStat />
            <Stat value={MARKETING_STATS.sportsCount} label="Sports covered" />
            <Stat value={MARKETING_STATS.languagesCount} label="Languages" />
            <Stat value="Daily" label="Top Picks, every day" />
          </div>
        </div>

        {/* ---------------- HOW IT WORKS ---------------- */}
        <section className={styles.block} id="how">
          <div className={styles.wrap}>
            <SectionHead
              eyebrow="How it works"
              title="From fixtures to a forecast in three steps"
              body="No spreadsheets, no guesswork. Just pick a match and let the duck do the math."
            />
            <div className={styles.steps}>
              {STEPS.map((s, i) => (
                <div key={s.title} className={styles.step}>
                  <div className={styles.stepNum}>{i + 1}</div>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- SPORTS ---------------- */}
        <section className={`${styles.block} ${styles.blockTight}`} id="sports">
          <div className={styles.wrap}>
            <SectionHead eyebrow="Coverage" title="Nine sports. Every major league." />
            <div className={styles.sports}>
              {SPORTS.map((s) => (
                <div key={s.name} className={styles.sport}>
                  <div className={styles.sportEmoji} aria-hidden="true">
                    {s.emoji}
                  </div>
                  <span>{s.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- TRACK RECORD ---------------- */}
        <section className={styles.block} id="accuracy">
          <div className={styles.wrap}>
            <div className={styles.acc}>
              <div>
                <span className="eyebrow">Track record</span>
                <h2 className={styles.accTitle}>We publish our hit rate. Most don&rsquo;t.</h2>
                <p className={styles.accBody}>
                  Every Top Pick gets graded once the game ends, and we show the running accuracy
                  inside the app. Trust is earned with receipts, not promises.
                </p>
                <Link className={styles.accLink} to="/app">
                  See the full track record →
                </Link>
              </div>

              <AccuracyWidget variant="card" />
            </div>
          </div>
        </section>

        {/* ---------------- FEATURES ---------------- */}
        <section className={`${styles.block} ${styles.blockTight}`}>
          <div className={styles.wrap}>
            <SectionHead eyebrow="Why Don Quack" title="Built for people who hate guessing" />
            <div className={styles.feats}>
              {FEATURES.map((f) => (
                <div key={f.title} className={styles.feat}>
                  <div className={styles.featIcon} aria-hidden="true">
                    {f.icon}
                  </div>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- PRICING ---------------- */}
        <section className={styles.block} id="pricing">
          <div className={styles.wrap}>
            <SectionHead
              eyebrow="Pricing"
              title="Start free. Upgrade when you're hooked."
              body="Pay with crypto. Change or stop your plan anytime from your account."
            />
            <div className={styles.tiers}>
              {MARKETING_TIERS.map((t) => (
                <TierCard
                  key={t.id}
                  name={t.name}
                  price={t.price}
                  period={t.period}
                  features={t.features}
                  featured={t.featured}
                  ctaLabel={t.cta}
                  onCta={() => navigate('/app')}
                />
              ))}
            </div>
            <p className={styles.priceNote}>
              Prices in USD. You&rsquo;ll see the exact amount before paying.
            </p>
          </div>
        </section>

        {/* ---------------- FAQ ---------------- */}
        <section className={`${styles.block} ${styles.blockTight}`} id="faq">
          <div className={styles.wrap}>
            <SectionHead eyebrow="FAQ" title="Questions, answered" />
            <div className={styles.faq}>
              {FAQ.map((item, i) => (
                <details key={item.q} open={i === 0}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- FINAL CTA ---------------- */}
        <section className={`${styles.block} ${styles.final}`}>
          <div className={styles.wrap}>
            <img src="/duck.webp" alt="" width="96" height="96" loading="lazy" />
            <h2 className={styles.finalTitle}>Make your next call a smart one.</h2>
            <p className={styles.finalBody}>
              Free to start. Your first forecast is 10 seconds away.
            </p>
            <div className={styles.finalCta}>
              <Button to="/app" size="lg">
                Start free 🦆
              </Button>
              <Button href={TELEGRAM_BOT_URL} variant="secondary" size="lg">
                Follow on Telegram
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  );
}

function SectionHead({ eyebrow, title, body }) {
  return (
    <div className={styles.secHead}>
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
    </div>
  );
}

/**
 * Headline accuracy for the trust strip.
 *
 * Shows the real percentage when the public endpoint has graded data, and an
 * honest non-numeric statement otherwise.
 */
function AccuracyStat() {
  const [accuracy, setAccuracy] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    getPublicAccuracy({ signal: controller.signal })
      .then((raw) => setAccuracy(normaliseAccuracy(raw)))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Nothing graded yet, or the call failed.
  if (!accuracy) {
    return <Stat value="Graded" label="Every Top Pick, publicly" gradient />;
  }

  // Graded, but too small a sample to publish a percentage.
  if (!accuracy.meetsFloor) {
    return <Stat value={accuracy.graded} label="Top Picks graded so far" gradient />;
  }

  return (
    <Stat
      value={`${accuracy.pct}%`}
      label={`Top Picks accuracy · ${accuracy.graded} graded`}
      gradient
    />
  );
}

function Stat({ value, label, gradient = false }) {
  return (
    <div className={styles.stat}>
      <b className={`mono-num ${gradient ? 'grad-text' : ''}`}>{value}</b>
      <span>{label}</span>
    </div>
  );
}
